import { Breadcrumb, BreadcrumbGroup } from "@amzn/meridian";
import Column from "@amzn/meridian/column";
import Divider from "@amzn/meridian/divider";
import Row from "@amzn/meridian/row";
import React, { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { hasNoLLMClarification } from "src/components/shared/groundTruth/reviewTable/helpers";
import { TOAST_TIMEOUT } from "src/config/Toast";
import {
  extractTextHighlightByFieldDefinition,
  getRandomInsight,
  getInsightBySourceId,
  setListViewIndex,
  setMoveToNextWhenSave,
  updateInsight,
} from "src/store/modelLifeCycleGroundTruthSlice";
import { getSiteDetails, setSelectedModelId } from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import ReviewHeader from "src/components/shared/groundTruth/reviewHeader/reviewHeader";
import ReviewTable from "src/components/shared/groundTruth/reviewTable/reviewTable";
import ReviewText from "src/components/shared/groundTruth/reviewText/reviewText";

const AutomatedJobReview = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    selectedGroundTruthData,
    getListViewLoading,
    getGroundTruthDataLoading,
    showHighlight,
    textHighlightLoading,
  } = useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);
  const { selectedModel, getModelByIdLoading, getSiteDetailsLoading } = useAppSelector(
    (state) => state.modelSlice,
  );
  const { jobId, sourceId } = useParams();
  const [searchParams] = useSearchParams();

  // Determine access type based on URL parameters
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const isDirectAccess = !startDate && !endDate;

  useEffect(() => {
    if (jobId && sourceId && isDirectAccess) {
      // Use new API when no date parameters are provided
      dispatch(
        getInsightBySourceId({
          definition_id: jobId,
          source_id: sourceId,
        }),
      );
      // For direct access, set button to just "Save" (no next navigation)
      dispatch(setMoveToNextWhenSave(false));
    } else {
      // For random workflow, set button to "Save & Next"
      dispatch(setMoveToNextWhenSave(true));
    }
  }, [jobId, sourceId, searchParams]);

  useEffect(() => {
    // Only redirect if we're not currently loading data and still don't have modelId
    if (!selectedGroundTruthData.modelId && getGroundTruthDataLoading === "rejected") {
      navigate("/automatedJobs");
    }
    if (selectedGroundTruthData.modelId && selectedGroundTruthData.modelId != selectedModel.id) {
      dispatch(setSelectedModelId(selectedGroundTruthData.modelId));
    }
  }, [selectedGroundTruthData.modelId, getGroundTruthDataLoading]);

  useEffect(() => {
    if (
      getGroundTruthDataLoading === "fulfilled" &&
      showHighlight &&
      textHighlightLoading === "idle" &&
      hasNoLLMClarification(selectedGroundTruthData)
    ) {
      dispatch(
        extractTextHighlightByFieldDefinition({
          text: selectedGroundTruthData.sourceText,
          modelId: selectedGroundTruthData.modelId,
        }),
      );
    }
    if (showHighlight && textHighlightLoading === "fulfilled") {
      dispatch(
        createToast({
          type: "success",
          message: "LLM extract highlight success, click field name to view highlight",
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  }, [getGroundTruthDataLoading, showHighlight, textHighlightLoading]);

  useEffect(() => {
    if (getListViewLoading === "fulfilled" && getGroundTruthDataLoading === "fulfilled") {
      dispatch(setListViewIndex(sourceId));
    }
  }, [getListViewLoading, getGroundTruthDataLoading]);

  // this logic is only used in current safety sentinel data label
  // to override location list by site code or don't override if metadata has showFullLocationList
  useEffect(() => {
    if (
      getModelByIdLoading === "fulfilled" &&
      getGroundTruthDataLoading === "fulfilled" &&
      selectedGroundTruthData.metadata?.siteCode &&
      getSiteDetailsLoading === "pending" &&
      !selectedGroundTruthData.metadata?.showFullLocationList // Add this condition
    ) {
      dispatch(getSiteDetails(selectedGroundTruthData.metadata.siteCode));
    }
  }, [getModelByIdLoading, getGroundTruthDataLoading]);

  const onSaveClick = async () => {
    if (!jobId) return;
    const startDate = searchParams.get("startDate") || "";
    const endDate = searchParams.get("endDate") || "";

    // Determine if this is a selective review (accessed directly without date params)
    const isSelectiveReview = !startDate && !endDate;

    await dispatch(
      updateInsight({
        definition_id: jobId,
        ground_truth_data: selectedGroundTruthData,
        is_selected: isSelectiveReview, // true for direct access, false for random workflow
      }),
    ).unwrap();

    // Only navigate to next random insight if it's part of the random workflow (has dates)
    if (!isSelectiveReview) {
      const data = await dispatch(
        getRandomInsight({
          start_date: startDate,
          end_date: endDate,
          definition_id: jobId,
        }),
      ).unwrap();

      navigate(
        `/automatedJobs/${jobId}/${data.sourceId}?startDate=${startDate}&endDate=${endDate}`,
      );
    }
    // For selective reviews (direct access), just save and stay on the same page
  };

  return (
    <Column spacingInset={"500 400 0 400"} spacing={"none"}>
      <BreadcrumbGroup>
        <Breadcrumb onClick={() => navigate(`/automatedJobs`)}>Automated Jobs</Breadcrumb>
        <Breadcrumb onClick={() => navigate(`/automatedJobs/${jobId}`)}>{jobId}</Breadcrumb>
        <Breadcrumb
          onClick={() =>
            navigate(
              `/automatedJobs/${selectedGroundTruthData.modelId}/${selectedGroundTruthData.sourceId}`,
            )
          }
        >
          {sourceId}
        </Breadcrumb>
      </BreadcrumbGroup>
      {jobId && <ReviewHeader onSaveClick={onSaveClick} showMoveToNextToggle={!isDirectAccess} />}
      <Divider></Divider>
      <Row
        spacingInset={"0"}
        widths={["35%", "fit", "fill"]}
        alignmentVertical="top"
        spacing={"none"}
      >
        <ReviewText />
        <div className="audit-review-split-line"></div>
        <Column height={"76vh"} overflowY="auto">
          <ReviewTable />
        </Column>
      </Row>
      <Divider />
    </Column>
  );
};

export default AutomatedJobReview;
